import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faDollarSign,
  faComments,
  faCheck,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

function Bids() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [bidsPerPage, setBidsPerPage] = useState(10);
  const [totalBids, setTotalBids] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });
  const [filteredBids, setFilteredBids] = useState([]);
  const [counterOffersHistory, setCounterOffersHistory] = useState({});
  const [newCounterOfferAmounts, setNewCounterOfferAmounts] = useState({});
  const [counterOfferCurrencies, setCounterOfferCurrencies] = useState({});
  const [showCounterOffer, setShowCounterOffer] = useState({});

  // Fetch bids from Supabase
  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      setLoading(true);

      // First, get the count
      const { count, error: countError } = await supabase
        .from('bids')
        .select('*', { count: 'exact', head: true });

      if (countError) {
        throw countError;
      }

      // Then get the actual data with driver info
      const { data, error } = await supabase
        .from('bids')
        .select(`*`)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setTotalBids(count);
      setBids(data);

      // Fetch counter offers for all bids
      await fetchCounterOffers(data.map((bid) => bid.id));
    } catch (error) {
      console.error('Error fetching bids:', error);
      setError('Failed to load bids');
    } finally {
      setLoading(false);
    }
  };

  const fetchCounterOffers = async (bidIds) => {
    try {
      const { data, error } = await supabase
        .from('counter_offers')
        .select('*')
        .in('bid_id', bidIds)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group counter offers by bid_id
      const counterOffersByBid = {};
      data.forEach((offer) => {
        if (!counterOffersByBid[offer.bid_id]) {
          counterOffersByBid[offer.bid_id] = [];
        }
        counterOffersByBid[offer.bid_id].push(offer);
      });

      setCounterOffersHistory(counterOffersByBid);

      // Initialize counter offer amounts and currencies based on the latest counter offer
      const initialAmounts = {};
      const initialCurrencies = {};
      Object.entries(counterOffersByBid).forEach(([bidId, offers]) => {
        if (offers.length > 0) {
          const latestOffer = offers[offers.length - 1];
          initialAmounts[bidId] = latestOffer.amount;
          initialCurrencies[bidId] = latestOffer.currency;
        }
      });

      setNewCounterOfferAmounts((prev) => ({ ...prev, ...initialAmounts }));
      setCounterOfferCurrencies((prev) => ({ ...prev, ...initialCurrencies }));
    } catch (error) {
      console.error('Error fetching counter offers:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Define filterBids with useCallback
  const filterBids = useCallback(() => {
    let filtered = [...bids];

    // Search filter (load reference, driver name)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (bid) =>
          bid.load?.formatted_id?.toLowerCase().includes(searchLower) ||
          `${bid.driver_info?.first_name} ${bid.driver_info?.last_name}`
            .toLowerCase()
            .includes(searchLower) ||
          bid.driver_info?.phone_number?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter((bid) => bid.status === filters.status);
    }

    setFilteredBids(filtered);
  }, [bids, filters]);

  // Apply filters when bids or filters change
  useEffect(() => {
    filterBids();
  }, [bids, filters, filterBids]);

  // Reset to first page when filters or bidsPerPage change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, bidsPerPage]);

  // Handle change in number of bids per page
  const handleBidsPerPageChange = (e) => {
    setBidsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Pagination logic
  const indexOfLastBid = currentPage * bidsPerPage;
  const indexOfFirstBid = indexOfLastBid - bidsPerPage;
  const currentBids = filteredBids.slice(indexOfFirstBid, indexOfLastBid);
  const totalPages = Math.ceil(filteredBids.length / bidsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getBidStatusClass = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'countered':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleAcceptBid = async (bid) => {
    try {
      // Update load status
      const { error: loadError } = await supabase
        .from('loads')
        .update({
          status: 'waitingDriver',
          accepted_bid_id: bid.id,
          accepted_driver: bid.bid_type !== 'company' ? bid.driver_info : null,
          company_id: bid.bid_type === 'company' ? bid.driver_info.id : null,
        })
        .eq('id', bid.load.id);

      if (loadError) throw loadError;

      // Update bid status
      const { error: bidError } = await supabase
        .from('bids')
        .update({ status: 'accepted' })
        .eq('id', bid.id);

      if (bidError) throw bidError;

      // Refresh the bids list
      await fetchBids();
    } catch (error) {
      console.error('Error accepting bid:', error);
    }
  };

  const handleRejectBid = async (bid) => {
    try {
      // Update bid status to rejected
      const { error: bidError } = await supabase
        .from('bids')
        .update({ status: 'rejected' })
        .eq('id', bid.id);

      if (bidError) throw bidError;

      // Refresh the bids list
      await fetchBids();
    } catch (error) {
      console.error('Error rejecting bid:', error);
    }
  };

  const handleCounterOffer = async (bid, amount) => {
    try {
      if (!user) {
        console.error('No user found');
        return;
      }

      const { data, error } = await supabase.from('counter_offers').insert({
        bid_id: bid.id,
        amount: parseFloat(amount),
        currency: counterOfferCurrencies[bid.id] || 'EUR',
        stop_negotiation: false,
        created_by: user.id,
      });

      if (error) throw error;

      // Update bid status to countered

      // Hide counter offer form
      setShowCounterOffer((prev) => ({ ...prev, [bid.id]: false }));

      // Refresh the bids list
      await fetchBids();
    } catch (error) {
      console.error('Error sending counter offer:', error);
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">Teklifler</h1>
              <p className="text-sm text-gray-500">
                Toplam Teklif: {totalBids}
              </p>
            </div>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arama
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Yük referansı, sürücü adı veya telefon ile ara..."
                    className="w-full px-4 py-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="pending">Beklemede</option>
                  <option value="accepted">Kabul Edildi</option>
                  <option value="rejected">Reddedildi</option>
                  <option value="countered">Karşı Teklif</option>
                </select>
              </div>
            </div>
          </div>

          {/* Bids List */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Teklifler yükleniyor...</div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Yük Bilgisi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Sürücü
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Teklif
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tarih
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentBids.map((bid) => (
                      <tr key={bid.id}>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-blue-600">
                            {bid.load_details?.formatted_id || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {bid.load_details?.loading_country} →{' '}
                            {bid.load_details?.discharge_country}
                          </div>
                          <div className="text-xs text-gray-400">
                            {bid.load_details?.pickup_location} →{' '}
                            {bid.load_details?.delivery_location}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {bid.driver_info?.first_name}{' '}
                            {bid.driver_info?.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {bid.driver_info?.phone_number || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {bid.driver_info?.email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {bid.amount} {bid.currency}
                          </div>
                          {counterOffersHistory[bid.id] &&
                            counterOffersHistory[bid.id].length > 0 && (
                              <div className="mt-2">
                                <div className="flex items-center mb-1">
                                  <FontAwesomeIcon
                                    icon={faComments}
                                    className="mr-1 text-blue-500"
                                  />
                                  <span className="text-xs font-medium text-blue-600">
                                    Karşı Teklifler (
                                    {counterOffersHistory[bid.id].length})
                                  </span>
                                </div>
                                <div className="pl-5 border-l-2 border-blue-200">
                                  {counterOffersHistory[bid.id].map(
                                    (offer, index) => (
                                      <div
                                        key={offer.id}
                                        className="text-xs mb-1"
                                      >
                                        <div className="flex justify-between">
                                          <span
                                            className={`font-medium ${index === counterOffersHistory[bid.id].length - 1 ? 'text-blue-600' : 'text-gray-600'}`}
                                          >
                                            {offer.amount} {offer.currency}
                                          </span>
                                          <span className="text-gray-500 text-xs">
                                            {formatDate(offer.created_at)}
                                          </span>
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getBidStatusClass(
                              bid.status
                            )}`}
                          >
                            {bid.status === 'pending'
                              ? 'Beklemede'
                              : bid.status === 'accepted'
                                ? 'Kabul Edildi'
                                : bid.status === 'rejected'
                                  ? 'Reddedildi'
                                  : 'Karşı Teklif'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {formatDate(bid.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            {bid.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleAcceptBid(bid)}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center justify-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="mr-1"
                                  />
                                  Kabul Et
                                </button>
                                <button
                                  onClick={() => handleRejectBid(bid)}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 flex items-center justify-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faTimes}
                                    className="mr-1"
                                  />
                                  Reddet
                                </button>
                                <button
                                  onClick={() =>
                                    setShowCounterOffer((prev) => ({
                                      ...prev,
                                      [bid.id]: !prev[bid.id],
                                    }))
                                  }
                                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center"
                                >
                                  <FontAwesomeIcon
                                    icon={faDollarSign}
                                    className="mr-1"
                                  />
                                  Karşı Teklif
                                </button>
                              </>
                            )}

                            {showCounterOffer[bid.id] && (
                              <div className="mt-2 p-2 bg-gray-50 rounded border">
                                <div className="flex items-center space-x-2 mb-2">
                                  <input
                                    type="number"
                                    value={newCounterOfferAmounts[bid.id] || ''}
                                    onChange={(e) =>
                                      setNewCounterOfferAmounts((prev) => ({
                                        ...prev,
                                        [bid.id]: e.target.value,
                                      }))
                                    }
                                    className="w-24 px-2 py-1 border rounded"
                                    placeholder="Miktar"
                                  />
                                  <select
                                    value={
                                      counterOfferCurrencies[bid.id] || 'EUR'
                                    }
                                    onChange={(e) =>
                                      setCounterOfferCurrencies((prev) => ({
                                        ...prev,
                                        [bid.id]: e.target.value,
                                      }))
                                    }
                                    className="px-2 py-1 border rounded"
                                  >
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                    <option value="TRY">TRY</option>
                                  </select>
                                </div>
                                <div className="flex justify-end">
                                  <button
                                    onClick={() =>
                                      handleCounterOffer(
                                        bid,
                                        newCounterOfferAmounts[bid.id]
                                      )
                                    }
                                    className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                  >
                                    Gönder
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                <div className="px-6 py-4 flex items-center justify-between border-t">
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">
                      Sayfa başına:
                    </span>
                    <select
                      value={bidsPerPage}
                      onChange={handleBidsPerPageChange}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-700">
                      {indexOfFirstBid + 1}-
                      {Math.min(indexOfLastBid, filteredBids.length)} /{' '}
                      {filteredBids.length}
                    </span>
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Bids;
